import { useTranslations } from 'next-intl'
import { useAppLocale } from '@/components/i18n/locale-provider'
import { numberFormat } from '@/lib/i18n/format'
import { DATA_IN_INTEGRATION_DEFINITIONS } from '@/lib/integrations/catalog/data_in'
import type { IntegrationsTranslator } from '@/lib/integrations/integrations-translator'

/** New submissions a form accepts per minute, and a brand across its forms. */
const FORM_SUBMISSIONS_PER_MINUTE = 120
const BRAND_SUBMISSIONS_PER_MINUTE = 600

type GuideStep = Readonly<{ label: string; description: string }>

/**
 * The Framer setup guide: its steps, then what to check after a test. The
 * public page passes the steps in the reader's language
 * (`integrationsContent`); the signed-in app shows the catalog's English.
 */
export function FramerFormGuide({
  steps = DATA_IN_INTEGRATION_DEFINITIONS.framer.tutorialSteps,
}: {
  steps?: ReadonlyArray<GuideStep>
}) {
  const t = useTranslations('integrations')
  return (
    <section
      className="flex flex-col gap-5"
      aria-labelledby="framer-setup-heading"
    >
      <h2 id="framer-setup-heading" className="font-medium text-lg">
        {t('framer.guide.title')}
      </h2>
      <ol className="flex list-decimal flex-col gap-4 pl-5 text-sm">
        {steps.map((step) => (
          <li key={step.label} className="pl-1">
            <span className="font-medium">{step.label}</span>
            <p className="mt-1 text-muted-foreground">{step.description}</p>
          </li>
        ))}
      </ol>
      <p className="text-muted-foreground text-sm">
        {t('framer.guide.afterTest')}
      </p>
      <FramerFormHelp />
      <a
        className="text-sm underline underline-offset-4"
        href="https://www.framer.com/help/articles/framer-form-webhook-setup/"
        target="_blank"
        rel="noreferrer"
      >
        {t('framer.guide.docsLink')}
      </a>
    </section>
  )
}

/** Each troubleshooting entry's key in the `integrations` catalog (`framer.help`). */
const HELP_TOPICS = [
  'fieldMismatch',
  'signingFailure',
  'throttling',
  'noRuns',
] as const

type HelpTopic = (typeof HELP_TOPICS)[number]

/** A troubleshooting entry's answer; the throttling one names the limits. */
function helpAnswer(
  t: IntegrationsTranslator,
  topic: HelpTopic,
  limits: { perForm: string; perBrand: string }
): string {
  switch (topic) {
    case 'fieldMismatch':
      return t('framer.help.fieldMismatch.matchingRules')
    case 'throttling':
      return t('framer.help.throttling.body', limits)
    default:
      return t(`framer.help.${topic}.body`)
  }
}

export function FramerFormHelp() {
  const t = useTranslations('integrations')
  const locale = useAppLocale()
  const limits = {
    perForm: numberFormat(locale).format(FORM_SUBMISSIONS_PER_MINUTE),
    perBrand: numberFormat(locale).format(BRAND_SUBMISSIONS_PER_MINUTE),
  }
  return (
    <details className="border-t pt-4 text-sm">
      <summary className="cursor-pointer text-muted-foreground transition-colors hover:text-foreground">
        {t('framer.help.title')}
      </summary>
      <dl className="mt-4 grid gap-4">
        {HELP_TOPICS.map((topic) => (
          <div key={topic}>
            <dt className="font-medium">{t(`framer.help.${topic}.title`)}</dt>
            <dd className="text-muted-foreground">
              {helpAnswer(t, topic, limits)}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  )
}
